import React, { useState, useRef } from 'react';
import { View, Text, TextInput, StyleSheet, Modal, TouchableOpacity, ScrollView, Image } from 'react-native';
import BasicModalContainer from '../../../../common/components/basicmodal/BasicModalContainer';
import ModalHeader from '../../../../common/components/basicmodal/ModalHeader';
import ModalBody from '../../../../common/components/basicmodal/ModalBody';
import ModalFooter from '../../../../common/components/basicmodal/ModalFooter';
import { commonModalStyle } from '../../../../common/components/basicmodal';
import { Editor } from 'primereact/editor';
import { API_URL } from '../../../../common/constants/AppConstants';

const CONTENT_TYPES = [
  { label: 'HTML', value: 'html', icon: '🖊️', desc: 'Rich HTML with editor' },
  { label: 'Image', value: 'image', icon: '🖼️', desc: 'Upload image file' },
];

const LOCATION_SUGGESTIONS = ['home', 'header', 'footer', 'about', 'contact', 'sidebar', 'hero', 'banner', 'pricing', 'faq'];

const AddKeyModal = ({ isModalVisible, details, closeModal, onSave, brands = [], defaultBrandId = '' }) => {
  const [formData, setFormData] = useState(() => {
    if (details) {
      return {
        id: details.id,
        brand_id: details.brand_id ? String(details.brand_id) : '',
        key_name: details.key_name || '',
        location: details.location || '',
        page_name: details.page_name || '',
        section: details.section || '',
        content_type: details.content_type || 'html',
        field_type: details.field_type || 'textarea',
        key_value: details.key_value || '',
        description: details.description || '',
      };
    }
    return {
      id: null,
      brand_id: defaultBrandId || '',
      key_name: '',
      location: '',
      page_name: '',
      section: '',
      content_type: 'html',
      field_type: 'textarea',
      key_value: '',
      description: '',
    };
  });

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(() => {
    if (details?.image_url) {
      return details.image_url.startsWith('http') ? details.image_url : `${API_URL}${details.image_url}`;
    }
    return null;
  });
  const fileInputRef = useRef<any>(null);

  const [errors, setErrors] = useState<{
    brand_id?: string;
    key_name?: string;
    location?: string;
    page_name?: string;
    key_value?: string;
    image?: string;
  }>({});

  const handleContentTypeChange = (type: string) => {
    const fieldTypeMap = { text: 'text', html: 'textarea', image: 'file' };
    setFormData({ ...formData, content_type: type, field_type: fieldTypeMap[type] || 'text', key_value: '' });
    setImageFile(null);
    setImagePreview(null);
  };

  const handleImagePick = () => {
    if (typeof document !== 'undefined') {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/jpeg,image/png,image/gif,image/webp,image/svg+xml';
      input.onchange = (e: any) => {
        const file = e.target.files?.[0];
        if (file) {
          if (file.size > 10 * 1024 * 1024) {
            setErrors({ ...errors, image: 'File size must be under 10MB' });
            return;
          }
          setImageFile(file);
          setImagePreview(URL.createObjectURL(file));
          setErrors({ ...errors, image: null });
        }
      };
      input.click();
    }
  };

  const validateForm = () => {
    const newErrors: any = {};

    if (details) {
      if (!formData.brand_id) newErrors.brand_id = 'Brand is required';
      if (!formData.location.trim()) newErrors.location = 'Location is required';
    } else {
      if (!formData.page_name.trim()) newErrors.page_name = 'Page name is required';
    }
    if (!formData.key_name.trim()) newErrors.key_name = 'Key name is required';

    if (formData.content_type === 'image') {
      if (!imageFile && !imagePreview) newErrors.image = 'Image file is required';
    } else {
      if (!formData.key_value.trim()) newErrors.key_value = 'Key value is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return;

    const fd = new FormData();
    const isEdit = !!formData.id;

    if (isEdit) {
      fd.append('id', String(formData.id));
      fd.append('brand_id', formData.brand_id || '');
      fd.append('location', (formData.location || '').trim());
    } else {
      fd.append('page_name', (formData.page_name || '').trim());
      if (formData.section.trim()) fd.append('section', formData.section.trim());
      fd.append('global', '1');
    }
    fd.append('key_name', (formData.key_name || '').trim());
    fd.append('content_type', formData.content_type || 'text');
    fd.append('field_type', formData.field_type || 'text');
    fd.append('description', (formData.description || '').trim());
    fd.append('key_value', formData.content_type === 'image' ? '' : (formData.key_value || '').trim());

    if (formData.content_type === 'image' && imageFile) {
      fd.append('image', imageFile);
    }

    onSave(fd);
  };

  const selectedBrandName = brands.find((b: any) => String(b.id) === formData.brand_id)?.brand;

  return (
    <Modal animationType="none" transparent={true} visible={isModalVisible}>
      <BasicModalContainer contentStyle={{ width: 720, maxWidth: '96%' }}>
        {/* Header */}
        <View style={styles.modalHeader}>
          <View style={styles.modalHeaderLeft}>
            <View style={styles.modalHeaderIcon}>
              <Text style={{ fontSize: 20 }}>{details ? '✏️' : '➕'}</Text>
            </View>
            <View>
              <Text style={styles.modalTitle}>{details ? 'Edit CMS Key' : 'Add New CMS Key'}</Text>
              <Text style={styles.modalSubtitle}>
                {details ? `Editing: ${details.key_name}` : 'Create a new content key for your brand'}
              </Text>
            </View>
          </View>
          <TouchableOpacity onPress={closeModal} style={styles.closeBtn}>
            <Text style={styles.closeBtnText}>✕</Text>
          </TouchableOpacity>
        </View>

        <ModalBody>
          <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
            <View style={styles.container}>

              {/* Section 1: Identity — Brand, Key Name, Type, Location */}
              <View style={styles.section}>
                <View style={styles.sectionHeader}>
                  <Text style={styles.sectionNumber}>1</Text>
                  <Text style={styles.sectionTitle}>Identity</Text>
                  <Text style={styles.sectionSubtitle}>Brand, key name, type &amp; location</Text>
                </View>

                {details ? (
                  <>
                    {/* Edit mode: Brand + Key Name */}
                    <View style={styles.row2}>
                      <View style={[styles.formGroup, { flex: 1 }]}>
                        <Text style={styles.label}>Brand <Text style={styles.required}>*</Text></Text>
                        <select
                          value={formData.brand_id}
                          onChange={(e) => {
                            setFormData({ ...formData, brand_id: e.target.value });
                            if (errors.brand_id) setErrors({ ...errors, brand_id: null });
                          }}
                          disabled
                          style={{ ...styles.selectNative, ...(errors.brand_id ? styles.inputError : {}), opacity: 0.6, cursor: 'not-allowed' } as any}
                        >
                          <option value="">-- Select Brand --</option>
                          {brands.map((b: any) => (
                            <option key={b.id} value={b.id}>{b.brand}</option>
                          ))}
                        </select>
                        {errors.brand_id
                          ? <Text style={styles.errorText}>⚠ {errors.brand_id}</Text>
                          : selectedBrandName
                            ? <Text style={styles.successText}>✓ {selectedBrandName}</Text>
                            : null
                        }
                      </View>
                      <View style={[styles.formGroup, { flex: 1 }]}>
                        <Text style={styles.label}>Key Name <Text style={styles.required}>*</Text></Text>
                        <TextInput
                          style={[styles.input, { opacity: 0.6 }]}
                          value={formData.key_name}
                          editable={false}
                          placeholder="e.g., hero_title"
                        />
                        <Text style={styles.helpText}>🔒 Cannot be changed in edit mode</Text>
                      </View>
                    </View>

                    {/* Edit mode: Content Type (read-only) */}
                    <View style={styles.formGroup}>
                      <Text style={styles.label}>Content Type <Text style={styles.required}>*</Text></Text>
                      <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#f0f4f8', borderWidth: 1, borderColor: '#dde1e7', borderRadius: 8, paddingHorizontal: 14, paddingVertical: 10, gap: 10 }}>
                        <Text style={{ fontSize: 18 }}>
                          {CONTENT_TYPES.find(ct => ct.value === formData.content_type)?.icon || '🖊️'}
                        </Text>
                        <Text style={{ fontSize: 14, fontWeight: '700', color: '#1a1a1a', flex: 1 }}>
                          {CONTENT_TYPES.find(ct => ct.value === formData.content_type)?.label || formData.content_type}
                        </Text>
                        <Text style={{ fontSize: 11, color: '#888', fontStyle: 'italic' }}>🔒 Cannot be changed in edit mode</Text>
                      </View>
                    </View>

                    {/* Edit mode: Location */}
                    <View style={[styles.formGroup, { marginBottom: 0 }]}>
                      <Text style={styles.label}>Location <Text style={styles.required}>*</Text></Text>
                      <TextInput
                        style={[styles.input, errors.location && styles.inputError]}
                        value={formData.location}
                        onChangeText={(text) => {
                          setFormData({ ...formData, location: text });
                          if (errors.location) setErrors({ ...errors, location: null });
                        }}
                        placeholder="e.g., home, header, footer"
                      />
                      {errors.location ? <Text style={styles.errorText}>⚠ {errors.location}</Text> : null}
                      <View style={styles.suggestionRow}>
                        <Text style={styles.helpText}>Quick pick: </Text>
                        {LOCATION_SUGGESTIONS.map((loc) => (
                          <TouchableOpacity
                            key={loc}
                            onPress={() => setFormData({ ...formData, location: loc })}
                            style={[styles.suggestionChip, formData.location === loc && styles.suggestionChipActive]}
                          >
                            <Text style={[styles.suggestionChipText, formData.location === loc && styles.suggestionChipTextActive]}>{loc}</Text>
                          </TouchableOpacity>
                        ))}
                      </View>
                    </View>
                  </>
                ) : (
                  <>
                    {/* Create mode: Page Name + Key Name */}
                    <View style={styles.row2}>
                      <View style={[styles.formGroup, { flex: 1 }]}>
                        <Text style={styles.label}>Page Name <Text style={styles.required}>*</Text></Text>
                        <TextInput
                          style={[styles.input, errors.page_name && styles.inputError]}
                          value={formData.page_name}
                          onChangeText={(text) => {
                            setFormData({ ...formData, page_name: text });
                            if (errors.page_name) setErrors({ ...errors, page_name: null });
                          }}
                          placeholder="e.g., home, about, contact"
                        />
                        {errors.page_name
                          ? <Text style={styles.errorText}>⚠ {errors.page_name}</Text>
                          : <Text style={styles.helpText}>The page this key belongs to</Text>
                        }
                      </View>
                      <View style={[styles.formGroup, { flex: 1 }]}>
                        <Text style={styles.label}>Key Name <Text style={styles.required}>*</Text></Text>
                        <TextInput
                          style={[styles.input, errors.key_name && styles.inputError]}
                          value={formData.key_name}
                          onChangeText={(text) => {
                            const slug = text.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
                            setFormData({ ...formData, key_name: slug });
                            if (errors.key_name) setErrors({ ...errors, key_name: null });
                          }}
                          placeholder="e.g., hero_title"
                        />
                        {errors.key_name
                          ? <Text style={styles.errorText}>⚠ {errors.key_name}</Text>
                          : <Text style={styles.helpText}>Auto-formatted to snake_case</Text>
                        }
                      </View>
                    </View>

                    {/* Create mode: Section (optional) */}
                    <View style={styles.formGroup}>
                      <Text style={styles.label}>Section</Text>
                      <TextInput
                        style={styles.input}
                        value={formData.section}
                        onChangeText={(text) => setFormData({ ...formData, section: text })}
                        placeholder="e.g., hero, banner, footer (optional)"
                      />
                      <Text style={styles.helpText}>Optional grouping within the page</Text>
                    </View>

                    {/* Create mode: Content Type */}
                    <View style={styles.formGroup}>
                      <Text style={styles.label}>Content Type <Text style={styles.required}>*</Text></Text>
                      <View style={styles.typeCardRow}>
                        {CONTENT_TYPES.map((ct) => (
                          <TouchableOpacity
                            key={ct.value}
                            style={[styles.typeCard, formData.content_type === ct.value && styles.typeCardActive]}
                            onPress={() => handleContentTypeChange(ct.value)}
                          >
                            <Text style={styles.typeCardIcon}>{ct.icon}</Text>
                            <Text style={[styles.typeCardLabel, formData.content_type === ct.value && styles.typeCardLabelActive]}>{ct.label}</Text>
                            <Text style={styles.typeCardDesc}>{ct.desc}</Text>
                            {formData.content_type === ct.value && (
                              <View style={styles.typeCardCheck}>
                                <Text style={{ color: '#fff', fontSize: 10, fontWeight: '700' }}>✓</Text>
                              </View>
                            )}
                          </TouchableOpacity>
                        ))}
                      </View>
                    </View>
                  </>
                )}
              </View>

              {/* Section 2: Content Value */}
              <View style={styles.section}>
                <View style={styles.sectionHeader}>
                  <Text style={styles.sectionNumber}>2</Text>
                  <Text style={styles.sectionTitle}>Content Value</Text>
                  <Text style={styles.sectionSubtitle}>
                    {formData.content_type === 'text' ? 'Plain text — displayed as-is on the frontend' :
                     formData.content_type === 'html' ? 'Rich HTML — rendered with dangerouslySetInnerHTML on frontend' :
                     'Image — URL stored, displayed via <img> tag on frontend'}
                  </Text>
                </View>

                {/* Text */}
                {formData.content_type === 'text' && (
                  <View style={styles.formGroup}>
                    <View style={styles.labelRow}>
                      <Text style={styles.label}>Text Value <Text style={styles.required}>*</Text></Text>
                      <Text style={styles.charCount}>{formData.key_value.length} chars</Text>
                    </View>
                    <TextInput
                      style={[styles.textArea, errors.key_value && styles.inputError]}
                      value={formData.key_value}
                      onChangeText={(text) => {
                        setFormData({ ...formData, key_value: text });
                        if (errors.key_value) setErrors({ ...errors, key_value: null });
                      }}
                      placeholder="Enter the text content that will be displayed..."
                      multiline
                      numberOfLines={4}
                    />
                    {errors.key_value && <Text style={styles.errorText}>⚠ {errors.key_value}</Text>}
                  </View>
                )}

                {/* HTML */}
                {formData.content_type === 'html' && (
                  <View style={styles.formGroup}>
                    <Text style={styles.label}>HTML Content <Text style={styles.required}>*</Text></Text>
                    {errors.key_value && <Text style={styles.errorText}>⚠ {errors.key_value}</Text>}
                    <View style={[styles.editorWrapper, errors.key_value && styles.inputError]}>
                      <style>{`.ql-editor { background: #ffffff !important; color: #1a1a1a !important; min-height: 180px; } .ql-toolbar { background: #f8f9fa !important; }`}</style>
                      <Editor
                        value={formData.key_value}
                        onTextChange={(e) => {
                          setFormData({ ...formData, key_value: e.htmlValue || '' });
                          if (errors.key_value) setErrors({ ...errors, key_value: null });
                        }}
                        style={{ height: 220 }}
                        headerTemplate={
                          <>
                            <span className="ql-formats">
                              <select className="ql-header" defaultValue="">
                                <option value="1">Heading 1</option>
                                <option value="2">Heading 2</option>
                                <option value="">Normal</option>
                              </select>
                            </span>
                            <span className="ql-formats">
                              <button className="ql-bold" />
                              <button className="ql-italic" />
                              <button className="ql-underline" />
                              <button className="ql-strike" />
                            </span>
                            <span className="ql-formats">
                              <select className="ql-color" />
                              <select className="ql-background" />
                            </span>
                            <span className="ql-formats">
                              <button className="ql-align" value="" />
                              <button className="ql-align" value="center" />
                              <button className="ql-align" value="right" />
                              <button className="ql-align" value="justify" />
                            </span>
                            <span className="ql-formats">
                              <button className="ql-list" value="ordered" />
                              <button className="ql-list" value="bullet" />
                            </span>
                            <span className="ql-formats">
                              <button className="ql-link" />
                              <button className="ql-clean" />
                            </span>
                          </>
                        }
                      />
                    </View>
                  </View>
                )}

                {/* Image */}
                {formData.content_type === 'image' && (
                  <View style={styles.formGroup}>
                    <Text style={styles.label}>Image File <Text style={styles.required}>*</Text></Text>
                    {imagePreview ? (
                      <View style={styles.previewWrapper}>
                        <Image source={{ uri: imagePreview }} style={styles.previewImage} resizeMode="contain" />
                        <View style={styles.previewOverlay}>
                          <Text style={styles.previewFilename}>{imageFile?.name || 'Current image'}</Text>
                          <View style={styles.previewActions}>
                            <TouchableOpacity style={styles.replaceBtn} onPress={handleImagePick}>
                              <Text style={styles.replaceBtnText}>🔄 Replace</Text>
                            </TouchableOpacity>
                            <TouchableOpacity style={styles.removeImageBtn} onPress={() => { setImageFile(null); setImagePreview(null); }}>
                              <Text style={styles.removeImageText}>✕ Remove</Text>
                            </TouchableOpacity>
                          </View>
                        </View>
                      </View>
                    ) : (
                      <TouchableOpacity style={styles.uploadArea} onPress={handleImagePick}>
                        <Text style={styles.uploadAreaIcon}>📁</Text>
                        <Text style={styles.uploadAreaTitle}>Click to upload image</Text>
                        <Text style={styles.uploadAreaSub}>JPEG, PNG, GIF, WEBP, SVG — max 5MB</Text>
                      </TouchableOpacity>
                    )}
                    {errors.image && <Text style={styles.errorText}>⚠ {errors.image}</Text>}
                  </View>
                )}
              </View>

              {/* Section 3: Notes */}
              <View style={[styles.section, { marginBottom: 0 }]}>
                <View style={styles.sectionHeader}>
                  <Text style={styles.sectionNumber}>3</Text>
                  <Text style={styles.sectionTitle}>Notes</Text>
                  <Text style={styles.sectionSubtitle}>Optional description for team reference</Text>
                </View>
                <View style={styles.formGroup}>
                  <TextInput
                    style={styles.textArea}
                    value={formData.description}
                    onChangeText={(text) => setFormData({ ...formData, description: text })}
                    placeholder="Describe what this key is used for, where it appears, who manages it..."
                    multiline
                    numberOfLines={2}
                  />
                </View>
              </View>

            </View>
          </ScrollView>
        </ModalBody>

        {/* Footer */}
        <View style={styles.footer}>
          <View style={styles.footerLeft}>
            {Object.keys(errors).length > 0 && (
              <Text style={styles.footerErrorHint}>⚠ Please fix {Object.keys(errors).length} error{Object.keys(errors).length > 1 ? 's' : ''} above</Text>
            )}
          </View>
          <View style={styles.footerButtons}>
            <TouchableOpacity onPress={closeModal} style={styles.cancelButton}>
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={handleSave} style={styles.saveButton}>
              <Text style={styles.saveButtonText}>{details ? '💾 Update Key' : '➕ Add Key'}</Text>
            </TouchableOpacity>
          </View>
        </View>

      </BasicModalContainer>
    </Modal>
  );
};

const styles = StyleSheet.create({
  ...commonModalStyle,
  scrollView: { maxHeight: 560 },
  container: { width: '100%' },

  /* Modal Header */
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 24,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e8ecf0',
    backgroundColor: '#f8fafc',
  },
  modalHeaderLeft: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  modalHeaderIcon: {
    width: 42,
    height: 42,
    borderRadius: 10,
    backgroundColor: '#e3f2fd',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalTitle: { fontSize: 18, fontWeight: '700', color: '#1a1a2e' },
  modalSubtitle: { fontSize: 12, color: '#888', marginTop: 2 },
  closeBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: '#f0f0f0',
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeBtnText: { fontSize: 16, color: '#555', fontWeight: '600' },

  /* Sections */
  section: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#e8ecf0',
    borderRadius: 10,
    padding: 16,
    marginBottom: 14,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 14,
    gap: 10,
  },
  sectionNumber: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#2196f3',
    color: '#fff',
    fontSize: 12,
    fontWeight: '700',
    textAlign: 'center',
    lineHeight: 24,
  },
  sectionTitle: { fontSize: 14, fontWeight: '700', color: '#1a1a2e' },
  sectionSubtitle: { fontSize: 12, color: '#999', marginLeft: 2 },

  /* Layout helpers */
  row2: { flexDirection: 'row', gap: 14 },
  labelRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 },

  /* Form elements */
  formGroup: { marginBottom: 14 },
  label: { fontSize: 13, fontWeight: '600', marginBottom: 6, color: '#444' },
  required: { color: '#f44336' },
  input: {
    borderWidth: 1,
    borderColor: '#dde1e7',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    backgroundColor: '#fff',
  },
  inputError: { borderColor: '#f44336', borderWidth: 1.5 },
  disabledInput: { backgroundColor: '#f5f5f5', color: '#aaa', cursor: 'not-allowed' },
  textArea: {
    borderWidth: 1,
    borderColor: '#dde1e7',
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    minHeight: 90,
    textAlignVertical: 'top',
    backgroundColor: '#fff',
  },
  selectNative: {
    borderWidth: 1,
    borderColor: '#dde1e7',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    height: 42,
    backgroundColor: '#fff',
    width: '100%',
  },
  errorText: { color: '#e53935', fontSize: 12, marginTop: 4 },
  helpText: { fontSize: 11, color: '#888', marginTop: 3, fontStyle: 'italic' },
  successText: { fontSize: 11, color: '#2e7d32', marginTop: 3 },
  charCount: { fontSize: 11, color: '#aaa' },

  /* Location suggestions */
  suggestionRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 5, marginTop: 8, alignItems: 'center' },
  suggestionChip: {
    paddingHorizontal: 9,
    paddingVertical: 3,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#dde1e7',
    backgroundColor: '#f5f7fa',
  },
  suggestionChipActive: { borderColor: '#2196f3', backgroundColor: '#e3f2fd' },
  suggestionChipText: { fontSize: 11, color: '#666' },
  suggestionChipTextActive: { color: '#1565c0', fontWeight: '600' },

  /* Content type read-only badge (edit mode) */
  contentTypeBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f0f4f8',
    borderWidth: 1,
    borderColor: '#dde1e7',
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 10,
    gap: 10,
  },
  contentTypeBadgeIcon: { fontSize: 18 },
  contentTypeBadgeLabel: { fontSize: 14, fontWeight: '700', color: '#1a1a1a', flex: 1 },
  contentTypeBadgeLock: { fontSize: 11, color: '#888', fontStyle: 'italic' },

  /* Content type cards */
  typeCardRow: { flexDirection: 'row', gap: 10 },
  typeCard: {
    flex: 1,
    borderWidth: 1.5,
    borderColor: '#dde1e7',
    borderRadius: 10,
    padding: 14,
    alignItems: 'center',
    backgroundColor: '#f8fafc',
    position: 'relative',
  },
  typeCardActive: { borderColor: '#2196f3', backgroundColor: '#e8f4fd' },
  typeCardIcon: { fontSize: 24, marginBottom: 6 },
  typeCardLabel: { fontSize: 13, fontWeight: '600', color: '#444', marginBottom: 3 },
  typeCardLabelActive: { color: '#1565c0' },
  typeCardDesc: { fontSize: 11, color: '#999', textAlign: 'center' },
  typeCardCheck: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: '#2196f3',
    alignItems: 'center',
    justifyContent: 'center',
  },

  /* Editor */
  editorWrapper: {
    borderWidth: 1,
    borderColor: '#dde1e7',
    borderRadius: 8,
    overflow: 'hidden',
    marginTop: 4,
    backgroundColor: '#ffffff',
  },
  /* Usage hint box */
  usageBox: {
    marginTop: 10,
    backgroundColor: '#f0f4f8',
    borderRadius: 6,
    borderLeftWidth: 3,
    borderLeftColor: '#2196f3',
    padding: 10,
  },
  usageLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: '#1565c0',
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  usageCode: {
    fontSize: 11,
    color: '#374151',
    fontFamily: 'monospace',
    backgroundColor: '#e8edf2',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 3,
    marginTop: 3,
  },

  /* Image upload */
  uploadArea: {
    borderWidth: 2,
    borderColor: '#90caf9',
    borderStyle: 'dashed',
    borderRadius: 10,
    padding: 28,
    alignItems: 'center',
    backgroundColor: '#f0f7ff',
    marginTop: 4,
  },
  uploadAreaIcon: { fontSize: 32, marginBottom: 8 },
  uploadAreaTitle: { fontSize: 14, fontWeight: '600', color: '#1976d2', marginBottom: 4 },
  uploadAreaSub: { fontSize: 12, color: '#90a4ae' },
  previewWrapper: {
    borderWidth: 1,
    borderColor: '#e0e0e0',
    borderRadius: 10,
    overflow: 'hidden',
    backgroundColor: '#fafafa',
    marginTop: 4,
  },
  previewImage: { width: '100%', height: 200 },
  previewOverlay: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: '#f5f5f5',
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
  },
  previewFilename: { fontSize: 12, color: '#555', flex: 1 },
  previewActions: { flexDirection: 'row', gap: 8 },
  replaceBtn: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    backgroundColor: '#e3f2fd',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#90caf9',
  },
  replaceBtnText: { color: '#1565c0', fontSize: 12, fontWeight: '600' },
  removeImageBtn: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    backgroundColor: '#ffebee',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#ef9a9a',
  },
  removeImageText: { color: '#c62828', fontSize: 12, fontWeight: '600' },

  /* Footer */
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 14,
    borderTopWidth: 1,
    borderTopColor: '#e8ecf0',
    backgroundColor: '#f8fafc',
  },
  footerLeft: { flex: 1 },
  footerErrorHint: { fontSize: 12, color: '#e53935' },
  footerButtons: { flexDirection: 'row', gap: 10 },
  cancelButton: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ccc',
    backgroundColor: '#fff',
  },
  cancelButtonText: { fontSize: 14, color: '#666', fontWeight: '600' },
  saveButton: {
    paddingHorizontal: 24,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: '#2196f3',
  },
  saveButtonText: { fontSize: 14, color: '#fff', fontWeight: '600' },
});

export default React.memo(AddKeyModal);
